import React from "react";
import {
  WSLoader,
  WSPanel,
  WSSectionToolbar
} from "@wingspanhq/fe-component-library";
import {
  queriesAccountingConnections,
  queriesAccountingReferenceData
} from "@query";
import { useQueryFeatureFlags } from "@hooks/useQueryFeatureFlags";
import { selectorIsQuickbooksConnectionSynced } from "../../../Integrations/selectors/selectorIsQuickbooksConnectionSynced";
import { selectorQuickbooksConnection } from "../../../Integrations/selectors/selectorQuickbooksConnection";
import { IntegrationsSection } from "./index";
import { IntegrationResourceType } from "./types";

type IntegrationWidgetProps = {
  isInactive: boolean;
  payerId: string;
  customerId?: string;
  itemId?: string;
  engagementId?: string;
};

/**
 * The client's QuickBooks customer/item mapping.
 *
 * v3 has no dedicated QuickBooks service endpoint: the connection is derived
 * from `listAccountingConnections` (see [[selectorQuickbooksConnection]]) and the
 * selectable customers/items come from that connection's reference data. The
 * mapped ids themselves are NOT read here — they belong to the payer or the
 * assignment and are passed in by the screen.
 */
export const PayerIntegrationWidget: React.FC<IntegrationWidgetProps> = ({
  isInactive,
  payerId,
  customerId,
  engagementId,
  itemId
}) => {
  const queryFeatureFlags = useQueryFeatureFlags();
  // The list filter accepts `status` only, so the partner is narrowed client-side.
  const queryConnections =
    queriesAccountingConnections.listAccountingConnections.useQuery({});
  const connection = selectorQuickbooksConnection(queryConnections.data);
  const connectionId = connection?.id;

  const isQuickbooksActive =
    !!connection &&
    (queryFeatureFlags.data?.forceShowQBO ||
      selectorIsQuickbooksConnectionSynced(connection));

  const isReferenceDataEnabled = isQuickbooksActive && !!connectionId;

  const queryCustomers =
    queriesAccountingReferenceData.listAccountingCustomers.useQueryAll(
      { connectionId: connectionId as string },
      { enabled: isReferenceDataEnabled }
    );
  const queryItems =
    queriesAccountingReferenceData.listAccountingItems.useQueryAll(
      { connectionId: connectionId as string },
      { enabled: isReferenceDataEnabled }
    );

  if (!isQuickbooksActive) {
    return null;
  }

  if (queryCustomers.isLoading || queryItems.isLoading) {
    return (
      <>
        <WSSectionToolbar
          title="Integration mappings"
          button={{ label: "Edit", disabled: true }}
        />

        <WSPanel mb="XL">
          <WSLoader.Spinner size="M" />
        </WSPanel>
      </>
    );
  }

  // A reference read that errors leaves the panel out entirely rather than
  // rendering every mapping as "Not mapped", which would read as data loss.
  if (queryCustomers.isError || queryItems.isError) {
    return null;
  }

  const customers = queryCustomers.flatData ?? [];
  const items = queryItems.flatData ?? [];

  return (
    <IntegrationsSection
      isInactive={isInactive}
      resourceType={IntegrationResourceType.Payer}
      payerIntegration={{
        payerId,
        engagementId,
        customerId,
        itemId,
        customer: customers.find(customer => customer.id === customerId),
        item: items.find(item => item.id === itemId),
        defaultItem: items.find(
          item => item.id === connection?.mappingDefaults?.defaultItemId
        )
      }}
    />
  );
};
